import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ViewCard.css';
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube, FaTwitter, FaGithub, FaPhoneAlt, FaEnvelope, FaVideo, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import { BsMicrosoftTeams } from 'react-icons/bs';

const ViewCard = () => {
    const { uuid } = useParams();
    const [cardData, setCardData] = useState(null);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchCard = async () => {
            try {
                const token = localStorage.getItem('token'); // Get the JWT token from local storage
    
                const response = await axios.get(`${API_URL}/api/cards/uuid/${uuid}`, {
                    headers: {
                        'Authorization': `Bearer ${token}` // Include the token in the Authorization header
                    }
                });
    
                setCardData(response.data);
            } catch (error) {
                console.error('Error fetching card:', error);
                setError('Error fetching card data');
            }
        };
    
        fetchCard();
    }, [uuid, API_URL]);    

    const handleSendEmail = async () => {
        try {
            await axios.post(`${API_URL}/api/send-email`, {
                email,
                uuid: cardData.uuid,
                name: cardData.name,
            });
            alert('Email sent successfully!');
            setShowEmailForm(false);
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Error sending email');
        }
    };

    const handleAddContact = async () => {
        try {
            if (!cardData || !cardData.id) {
                alert("Card data hasn't loaded yet.");
                return;
            }

            // Make the API request with the ID pulled directly from state
            await axios.post(`${API_URL}/api/contacts`, {
                name: contactName,
                email: contactEmail,
                phone: contactPhone,
                cardId: cardData.id // ✅ Changed from cardId to cardData.id
            });

            alert('Contact added successfully!');
            setShowContactForm(false);
            setContactName('');
            setContactEmail('');
            setContactPhone('');
        } catch (error) {
            console.error('Error adding contact:', error);
            alert('Error adding contact');
        }
    }; 

    // 1. Add these event handlers right above your return statement
    const handleSaveToAppleWallet = () => {
        // Redirects directly to the pass download stream
        window.location.href = `${API_URL}/api/cards/uuid/${uuid}/apple-wallet`;
    };

    const handleSaveToGoogleWallet = () => {
        window.location.href = `${API_URL}/api/cards/uuid/${uuid}/google-wallet`;
    };
    
    const renderIcons = (cardData) => {
        const icons = [
            { name: 'linkedin', url: cardData.linkedin, icon: <FaLinkedin />, show: !!cardData.linkedin },
            { name: 'facebook', url: cardData.facebook, icon: <FaFacebook />, show: !!cardData.facebook },
            { name: 'instagram', url: cardData.instagram, icon: <FaInstagram />, show: !!cardData.instagram },
            { name: 'youtube', url: cardData.youtube, icon: <FaYoutube />, show: !!cardData.youtube },
            { name: 'twitter', url: cardData.twitter, icon: <FaTwitter />, show: !!cardData.twitter },
            { name: 'teams', url: cardData.teams, icon: <BsMicrosoftTeams />, show: !!cardData.teams },
            { name: 'zoom', url: cardData.zoom, icon: <FaVideo />, show: !!cardData.zoom },
            { name: 'github', url: cardData.github, icon: <FaGithub />, show: !!cardData.github }
        ];

        return icons
            .filter(iconObj => iconObj.show)
            .map(({ name, url, icon }) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="icon">
                    {icon}
                </a>
            ));
    };

    return (
        <div className="view-card-container">
            {error && <p className="error-message">{error}</p>}
            {cardData ? (
                <div className="card-details">
                    <div 
                        className="card-top-section" 
                        style={{ 
                            backgroundImage: cardData.backgroundImage ? `url(${cardData.backgroundImage})` : 'none',
                            backgroundColor: cardData.backgroundColor || '#fff',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}
                    />
                    <div className="card-bottom-section">
                        <div className="profile-header-view">
                            <h3>{cardData.name || "Untitled Profile"}</h3>
                            <p className="preview-headline-title">{cardData.position}</p>
                            {cardData.companyName && <p className="preview-headline-company">{cardData.companyName}</p>}
                        </div>

                        <hr className="preview-divider" />

                        <div className="contact-rows-stack">
                            {cardData.phone && (
                                <div className="contact-row-item">
                                    <span className="row-icon-wrapper"><FaPhoneAlt /></span>
                                    <a href={`tel:${cardData.phone}`}>{cardData.phone}</a>
                                </div>
                            )}
                            {cardData.email && (
                                <div className="contact-row-item">
                                    <span className="row-icon-wrapper"><FaEnvelope /></span>
                                    <a href={`mailto:${cardData.email}`}>{cardData.email}</a>
                                </div>
                            )}
                            {cardData.address && (
                                <div className="contact-row-item">
                                    <span className="row-icon-wrapper"><FaMapMarkerAlt /></span>
                                    <a href={`http://googleusercontent.com/maps.google.com/?q=${encodeURIComponent(cardData.address)}`} target="_blank" rel="noopener noreferrer">
                                        {cardData.address}
                                    </a>
                                </div>
                            )}
                            {cardData.website && (
                                <div className="contact-row-item">
                                    <span className="row-icon-wrapper"><FaGlobe /></span>
                                    <a href={cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}`} target="_blank" rel="noopener noreferrer">
                                        {cardData.website}
                                    </a>
                                </div>
                            )}
                        </div>

                        {renderIcons(cardData).length > 0 && <hr className="preview-divider" />}

                        <div className="social-icons-unique">
                            {renderIcons(cardData)}
                        </div>
                    </div>
                </div>
            ) : (
                <p>Loading...</p>
            )}
            
            <div className="actions-container">
                <button className="action-main-btn" onClick={() => setShowEmailForm(true)}>Receive via Email</button>
                <button className="action-main-btn" onClick={() => setShowContactForm(true)}>Send Contact Details</button>
                
                <button 
                    className="apple-wallet-btn" 
                    onClick={handleSaveToAppleWallet}
                >
                    Add to Apple Wallet
                </button>

                <button 
                    className="google-wallet-btn" 
                    onClick={handleSaveToGoogleWallet}
                >
                    Save to Google Wallet
                </button>
                <p className="footer-text">Made with Digicard</p>
            </div>

            {showEmailForm && (
                <div className="popup">
                    <div className="popup-content">
                        <h3>Receive via Email</h3>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                        />
                        <button onClick={handleSendEmail}>Receive via email</button>
                        <button className="close-btn" onClick={() => setShowEmailForm(false)}>Close</button>
                    </div>
                </div>
            )}

            {showContactForm && (
                <div className="popup">
                    <div className="popup-content">
                        <h3>Send Contact Details</h3>
                        <input
                            type="text"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            placeholder="Enter your name"
                        />
                        <input
                            type="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            placeholder="Enter your email"
                        />
                        <input
                            type="tel"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="Enter your phone number"
                        />
                        <button onClick={handleAddContact}>Send</button>
                        <button className="close-btn" onClick={() => setShowContactForm(false)}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewCard;
